import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Modal, Row, Col, Spinner } from "react-bootstrap";
import AppTable, { ColumnConfig } from "../../../../components/AppTable/AppTable";
import TextPill from "../../../../components/TextPill";
import { SectionPermissionTypeEnum } from "../../../../types/SectionPermissionTypesEnum";
import { truncateText } from "../../../../utils/textUtils";
import { getDemandsToAddInImprovementPlans } from "../../api/improvementPlanAPI";
import { ImprovementPlanDemandListItemDto } from "../../types";
import { SelectItemDto } from "../../../../types/SelectItemDto";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { setSearchText } from "../../../../redux/historicalSlice";
import { getStatusBudgeColorStyle } from "../../../claims/utils/claimStatusStyle";
import { ClaimStatusEnum } from "../../../claims/types/ClaimStatusEnum";
import FiltersContainer from "../../../../components/Filters/components/FiltersContainer";
import { setHideFilter } from "../../../../redux/filtersSlice";
import Search from "../../../../components/Search";
import { getFiltersImprovementPlansDemands } from "../../api/getFiltersImprovementPlansDemads";
import { FilterDto } from "../../../../types/FilterDto";

interface ImprovementPlanDemandsModalProps {
    show: boolean;
    assignedToOptions: SelectItemDto[];
    excludedDemandIds?: number[];
    onClose: () => void;
    onAddDemands: (demands: ImprovementPlanDemandListItemDto[]) => void;
}

export default function ImprovementPlanDemandsModal({
    show,
    assignedToOptions,
    excludedDemandIds = [],
    onClose,
    onAddDemands
}: ImprovementPlanDemandsModalProps) {
    const { t } = useTranslation(["improvementPlan", "claim", "common"]);
    const dispatch = useDispatch();
    const { improvementPlanId } = useParams<{ improvementPlanId?: string }>();
    const currentPage = useSelector((state: RootState) => state.historical.improvementPlanDemandsModal.currentPage);
    const itemsPerPage = useSelector((state: RootState) => state.historical.improvementPlanDemandsModal.itemsPerPage);
    const selectedFilters = useSelector((state: RootState) => state.filters.improvementPlanDemandsModal.selectedFilters);


    const [isLoadingDemands, setIsLoadingDemands] = useState(false);
    const [demandsToAdd, setDemandsToAdd] = useState<ImprovementPlanDemandListItemDto[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchText, setDebouncedSearchText] = useState("");
    const [filtersDemands, setFiltersDemands] = useState<FilterDto[]>([]);
   
    useEffect(() => {
        const load = async () => {
            const result = await getFiltersImprovementPlansDemands(t, assignedToOptions);
            setFiltersDemands(result);
        };

        load();
    }, [t, assignedToOptions]);

    const [totalPages, setTotalPages] = useState(1);

    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [selectedDemandsById, setSelectedDemandsById] = useState<Map<number, ImprovementPlanDemandListItemDto>>(new Map());

    const excludedDemandIdsSet = useMemo(() => new Set(excludedDemandIds), [excludedDemandIds]);

    const [isFilterVisible, setIsFilterVisible] = useState<boolean>(false);
    const isHideFilter = useSelector((state: RootState) => state.filters.improvementPlanDemandsModal.hideFilter);

    const handleClick = () => {
        dispatch(setHideFilter({ table: 'improvementPlanDemandsModal', hide: false }));
        setIsFilterVisible(!isFilterVisible);
    };


    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchText(searchTerm);
        }, 1000);

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    useEffect(() => {
        if (!show) return;

        setIsLoadingDemands(true);
        getDemandsToAddInImprovementPlans(improvementPlanId ? Number(improvementPlanId) : 0, {
            pageNumber: currentPage,
            pageSize: itemsPerPage,
            search: debouncedSearchText.trim(),
            filters :selectedFilters,
        })
            .then((response) => {
                const items = (response?.items ?? []).filter((demand) => !excludedDemandIdsSet.has(demand.id));
                setDemandsToAdd(items);
                setTotalPages(response?.totalPages ?? 1);
            })
            .finally(() => setIsLoadingDemands(false));
    }, [
        show,
        improvementPlanId,
        currentPage,
        itemsPerPage,
        selectedFilters,
        debouncedSearchText,
        excludedDemandIdsSet,
    ]);

    const toggleSelection = (demand: ImprovementPlanDemandListItemDto) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(demand.id)) {
                next.delete(demand.id);
            } else {
                next.add(demand.id);
            }
            return next;
        });

        setSelectedDemandsById((prev) => {
            const next = new Map(prev);
            if (next.has(demand.id)) {
                next.delete(demand.id);
            } else {
                next.set(demand.id, demand);
            }
            return next;
        });
    };

    const handleClose = () => {
        setSelectedIds(new Set());
        setSelectedDemandsById(new Map());
        onClose();
    };

    const handleAddSelection = () => {
        if (selectedIds.size === 0) {
            handleClose();
            return;
        }

        onAddDemands(Array.from(selectedDemandsById.values()));
        handleClose();
    };

    const columns: ColumnConfig<ImprovementPlanDemandListItemDto>[] = [
        {
            header: t("improvementPlan:demandsModal.columns.select"),
            key: "select",
            cellStyle: { verticalAlign: "middle" },
            render: (item) => (
                <Form.Check
                    className="d-flex align-items-center justify-content-center"
                    type="checkbox"
                    checked={selectedIds.has(item.id)}
                    onChange={() => toggleSelection(item)}
                />
            )
        },
        {
            header: t("common:form.fields.description"),
            key: "description",
            cellStyle: { verticalAlign: "middle" },
            render: (item) => (
                <span title={(item.comments ?? "")}>
                    {truncateText((item.comments ?? ""), 40) || "-"}
                </span>
            )
        },
        {
            header: t("improvementPlan:demandsModal.columns.demand"),
            key: "requestType",
            cellStyle: { verticalAlign: "middle" },
            render: (item) => (
                <span title={(item.requestType ?? "")}>
                    {item.requestType}
                </span>
            )
        },
        {
            header: t("common:form.fields.status"),
            key: "status",
            render: (item) => {
                const style = getStatusBudgeColorStyle(ClaimStatusEnum[item.status as keyof typeof ClaimStatusEnum]);
                return <TextPill
                    label={t(`claim:status.${item.status}`)}
                    backgroundColor={style?.backgroundColor ?? "lightgrey"}
                    textColor={style?.color ?? "#fff"}
                    fontWeight={style?.fontWeight ?? 600}
                />
            }
        },
        {
            header: t("improvementPlan:demandsModal.columns.alert"),
            key: "hasAlert",
            render: (item) => (
                <TextPill
                    label={item.hasAlert ? t("common:yes") : t("common:no")}
                    backgroundColor={item.hasAlert ? "#ff7576" : "white"}
                    textColor={item.hasAlert ? "white" : "black"}
                    minWidth="70px"
                />
            )
        },
        {
            header: t("improvementPlan:demandsModal.columns.patientName"),
            key: "patientName",
            headerClassName: "text-center",
            cellClassName: "text-center",
             cellStyle: { verticalAlign: "middle" },
            render: (item) => item.patientName || "-"
        }
    ];

    const handleClearSearchText = () => {
        setSearchTerm('');
        dispatch(setSearchText({ table: "improvementPlanDemandsModal", text: '', goFirstPage: true }))
    }

    return (
        <Modal show={show} onHide={handleClose} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>{t("improvementPlan:demandsModal.title")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="mb-3">
                    <Col xs={11} md={11} className="mb-2">
                        <Search
                            searchText={searchTerm || ''}
                            onChange={setSearchTerm}
                            placeholder={t("improvementPlan:demandsModal.searchPlaceholder") }
                            onSearch={() =>
                                dispatch(
                                    setSearchText({
                                        table: "improvementPlanDemandsModal",
                                        text: searchTerm || '',
                                        goFirstPage: true
                                    })
                                )
                            }
                            onClear={handleClearSearchText}
                        />
                        
                    </Col>
                    <Col s={1} md={1}  >
                        <Button variant="" onClick={handleClick} aria-controls="collapse-filter-id" style={{ border: 0, padding: 0 }}>
                            <img
                                src="/filter.png"
                                alt="Filtar"
                                style={{ width: '35px' }}
                            />
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <div>
                        {!isHideFilter && filtersDemands && (
                            <div className="dashboard-section">
                                <FiltersContainer
                                    filters={filtersDemands}
                                    isFilterVisible={isFilterVisible && !isHideFilter}
                                    tableKey="improvementPlanDemandsModal"
                                    historicalKey="improvementPlanDemandsModal"
                                />
                            </div>
                        )}
                    </div>
                </Row>
                

                {isLoadingDemands ? (
                    <div className="d-flex justify-content-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <AppTable<ImprovementPlanDemandListItemDto>
                        data={demandsToAdd}
                        columns={columns}
                        emptyMessage={t("improvementPlan:demandsModal.noResults")}
                        t={t}
                        hasActions={false}
                        hasPagination={true}
                        currentPage={currentPage}
                        totalPages={Math.max(totalPages, 1)}
                        pageSize={itemsPerPage}
                        paginationType="improvementPlanDemandsModal"
                        sectionPermission={SectionPermissionTypeEnum.IMPROVEMENT_PLANS}
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    {t("common:buttons.buttonCancel")}
                </Button>
                <Button
                    variant="success"
                    onClick={handleAddSelection}
                    disabled={selectedIds.size === 0}
                >
                    {t("improvementPlan:demandsModal.addSelection")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

